/**
 * Deciding when someone has finished speaking.
 *
 * Deliberately not the Web Speech API. It exposes `onspeechend`, which is exactly the signal
 * wanted, but only as part of running Google's own recogniser — the audio would be
 * transcribed a second time, by a provider the account did not choose, outside the spend cap,
 * and only in Chrome. Far too much to accept for an endpoint marker.
 *
 * This works from the levels already being measured for the meter, so it costs nothing extra
 * and behaves the same everywhere. Kept as a pure state machine so it can be tested without
 * a microphone: feed it levels and timestamps, and it says what to do.
 */

export type ListenState =
  /** Nothing heard yet. Never stops here — a quiet room would end the recording instantly. */
  | 'waiting'
  /** Someone is talking. */
  | 'speaking'
  /** Speech, then a long enough pause. Time to stop. */
  | 'ended';

export interface EndpointConfig {
  /** Pause that counts as finished. Long enough to survive thinking mid-sentence. */
  silenceHoldMs: number;
  /** Absolute floor. Speech must clear this however quiet the room is. */
  minSpeechRms: number;
  /** How far above the room's own noise speech has to sit. */
  noiseMultiplier: number;
  /** Sustained loudness before we believe it, so a cough or a door does not start us. */
  speechOnsetMs: number;
  /** Hard ceiling, so a stuck-open microphone cannot record forever. */
  maxDurationMs: number;
}

export const DEFAULT_ENDPOINT_CONFIG: EndpointConfig = {
  // A second and a half: comfortably past a mid-sentence pause, short enough not to feel
  // like the app has stopped listening.
  silenceHoldMs: 1500,
  minSpeechRms: 0.02,
  noiseMultiplier: 2.5,
  speechOnsetMs: 180,
  maxDurationMs: 90_000,
};

export interface EndpointDetector {
  /** Feed one level reading. Returns the current state. */
  push(rms: number, atMs: number): ListenState;
  /** Threshold in use, once calibrated — useful for showing why nothing registered. */
  readonly threshold: number;
}

/**
 * The noise floor is tracked continuously, as the quietest level in a trailing window,
 * rather than measured once at the start.
 *
 * A fixed threshold fails in both directions. Calibrated in a quiet moment it sits too low,
 * and an air conditioner that starts afterwards holds the level above it forever — the
 * recording never sees a pause and never ends, which is exactly the failure this replaces.
 * Calibrated while someone is already talking it sits too high and never triggers at all.
 *
 * A trailing minimum sidesteps both: speech has gaps between words, so the quietest sample
 * in the last couple of seconds is the room rather than the speaker, and it follows the room
 * as it changes.
 */
const FLOOR_WINDOW_MS = 2_000;
/** Ignored below this: a genuinely silent input should not drag the floor to zero. */
const MIN_TRACKED_FLOOR = 0.0005;
/**
 * And a ceiling, because the trailing window is only a proxy for the room. Someone who
 * starts talking the instant they press record fills it with speech, and without this the
 * floor tracks their voice and the threshold climbs above it — the recording then never
 * starts. Anything steadier and louder than this is not room noise; it is a person.
 */
const MAX_TRACKED_FLOOR = 0.04;

export function createEndpointDetector(
  startedAtMs: number,
  config: EndpointConfig = DEFAULT_ENDPOINT_CONFIG,
): EndpointDetector {
  const window: Array<{ at: number; rms: number }> = [];
  let threshold = config.minSpeechRms;
  let loudSince: number | null = null;
  let quietSince: number | null = null;
  let hasSpoken = false;

  return {
    get threshold() {
      return threshold;
    },

    push(rms: number, atMs: number): ListenState {
      if (atMs - startedAtMs >= config.maxDurationMs) return 'ended';

      window.push({ at: atMs, rms });
      while (window.length > 1 && atMs - (window[0] as { at: number }).at > FLOOR_WINDOW_MS) {
        window.shift();
      }

      const trailingMin = window.reduce((lowest, s) => Math.min(lowest, s.rms), Infinity);
      const floor = Math.min(MAX_TRACKED_FLOOR, Math.max(MIN_TRACKED_FLOOR, trailingMin));
      threshold = Math.max(config.minSpeechRms, floor * config.noiseMultiplier);

      if (rms >= threshold) {
        quietSince = null;
        loudSince ??= atMs;
        if (atMs - loudSince >= config.speechOnsetMs) hasSpoken = true;
        return hasSpoken ? 'speaking' : 'waiting';
      }

      loudSince = null;
      if (!hasSpoken) return 'waiting';

      quietSince ??= atMs;
      return atMs - quietSince >= config.silenceHoldMs ? 'ended' : 'speaking';
    },
  };
}
